"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { collection, getDocs, query, where } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { AnimatePresence, motion } from "motion/react";
import { db, functions } from "@/lib/firebase";
import { startPaymentForBooking } from "@/lib/payment";
import { loadDraft, saveDraft, clearDraft } from "@/lib/draftStorage";
import { SHOW_PAYMENT_STEP } from "@/lib/featureFlags";
import { docToBatch } from "@/lib/batches";
import type { Batch } from "@/lib/types";
import { useTranslations } from "@/i18n/LocaleProvider";
import DemoPaymentStep from "@/components/DemoPaymentStep";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import FormSplitLayout from "@/components/FormSplitLayout";
import BatchListStep from "./steps/BatchListStep";
import BatchDetailsStep from "./steps/BatchDetailsStep";
import BatchPaymentStep from "./steps/BatchPaymentStep";
import BatchConfirmedStep from "./steps/BatchConfirmedStep";

type Step = "list" | "details" | "payment" | "demo_payment" | "confirmed";

interface EnrollResponse {
  booking: { id: string; reference: string };
  holdToken: string;
}

const DRAFT_KEY = "hy_batch_draft_v1";

interface BatchDraft {
  step: Step;
  selectedBatchId: string | null;
  name: string;
  email: string;
  emailVerified: boolean;
  phone: string;
  phoneVerified: boolean;
  termsAccepted: boolean;
  marketingOptIn: boolean;
  couponCode: string;
  bookingId: string | null;
  bookingReference: string | null;
  holdToken: string | null;
  demoOrder: { gatewayOrderId: string; amountRupees: number } | null;
}

export default function BatchesFlow() {
  const router = useRouter();
  const { t } = useTranslations();

  // Same plain-default-then-restore-in-an-effect split as BookingFlow.tsx, for the same reason
  // -- reading sessionStorage into a lazy useState initializer would make the client's first
  // render disagree with the (window-less) server-rendered HTML and trip a hydration mismatch.
  const [step, setStep] = useState<Step>("list");
  // See BookingFlow.tsx's identical isLoadingServices -- distinguishes "still fetching" from
  // "fetched, and there really are none right now" so the real empty-state copy doesn't flash
  // on screen during the initial fetch.
  const [isLoadingBatches, setIsLoadingBatches] = useState(true);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<Batch | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [emailVerified, setEmailVerified] = useState(false);
  const [phone, setPhone] = useState("");
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [marketingOptIn, setMarketingOptIn] = useState(false);
  const [couponCode, setCouponCode] = useState("");

  const [bookingId, setBookingId] = useState<string | null>(null);
  const [bookingReference, setBookingReference] = useState<string | null>(null);
  const [holdToken, setHoldToken] = useState<string | null>(null);
  const [demoOrder, setDemoOrder] = useState<{ gatewayOrderId: string; amountRupees: number } | null>(
    null
  );

  // See BookingFlow.tsx's identical ref -- gates the persistence-writing effect until the
  // restore effect has actually applied (or determined there's nothing to apply), so that
  // effect's first, pre-restore run doesn't clobber the real saved draft with fresh defaults.
  const restoredRef = useRef(false);

  // See BookingFlow.tsx's identical setup -- locks the step-content area to the tallest step
  // height seen so far (via ResizeObserver, so a step's own async content settling in later is
  // captured too) so switching steps doesn't visibly shrink/grow the form.
  const stepContainerRef = useRef<HTMLDivElement>(null);
  const [minStepHeight, setMinStepHeight] = useState<number | undefined>(undefined);
  useEffect(() => {
    const el = stepContainerRef.current;
    if (!el) return;
    const observer = new ResizeObserver(([entry]) => {
      const height = entry.contentRect.height;
      setMinStepHeight((prev) => (prev === undefined ? height : Math.max(prev, height)));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const restored = loadDraft<BatchDraft>(DRAFT_KEY);

    (async () => {
      const snap = await getDocs(
        query(collection(db, "batches"), where("status", "in", ["open", "full"]))
      );

      // Applied after the await above, not synchronously at the top of the effect -- see
      // BookingFlow.tsx's identical restore effect for why.
      if (restored) {
        setStep(restored.step ?? "list");
        setName(restored.name ?? "");
        setEmail(restored.email ?? "");
        setEmailVerified(restored.emailVerified ?? false);
        setPhone(restored.phone ?? "");
        setPhoneVerified(restored.phoneVerified ?? false);
        setTermsAccepted(restored.termsAccepted ?? false);
        setMarketingOptIn(restored.marketingOptIn ?? false);
        setCouponCode(restored.couponCode ?? "");
        setBookingId(restored.bookingId ?? null);
        setBookingReference(restored.bookingReference ?? null);
        setHoldToken(restored.holdToken ?? null);
        setDemoOrder(restored.demoOrder ?? null);
      }

      const loadedBatches = snap.docs.map(docToBatch);
      setBatches(loadedBatches);

      const restoredBatchId = restored?.selectedBatchId ?? null;
      if (restoredBatchId) {
        const match = loadedBatches.find((b) => b.id === restoredBatchId);
        if (match) setSelectedBatch(match);
      }
      restoredRef.current = true;
      setIsLoadingBatches(false);
    })().catch((err) => {
      setError(String(err));
      setIsLoadingBatches(false);
    });
  }, []);

  // Same "resume after a refresh" behavior as BookingFlow.tsx -- see draftStorage.ts.
  useEffect(() => {
    if (!restoredRef.current) return;
    if (step === "confirmed") {
      clearDraft(DRAFT_KEY);
      return;
    }
    saveDraft<BatchDraft>(DRAFT_KEY, {
      step,
      selectedBatchId: selectedBatch?.id ?? null,
      name,
      email,
      emailVerified,
      phone,
      phoneVerified,
      termsAccepted,
      marketingOptIn,
      couponCode,
      bookingId,
      bookingReference,
      holdToken,
      demoOrder,
    });
  }, [
    step,
    selectedBatch,
    name,
    email,
    emailVerified,
    phone,
    phoneVerified,
    termsAccepted,
    marketingOptIn,
    couponCode,
    bookingId,
    bookingReference,
    holdToken,
    demoOrder,
  ]);

  async function handleEnroll() {
    if (!selectedBatch) return;
    setLoading(true);
    setError(null);
    try {
      // No ensureSignedIn() here -- verifying the phone (PhoneOtpAuth, inside BatchDetailsStep)
      // already signed the customer in. See BookingFlow.tsx's handleCreateHold for the same
      // reasoning.
      const enrollInBatch = httpsCallable<
        {
          batchId: string;
          customerEmail: string | null;
          customerName: string;
          customerPhone: string;
          termsAccepted: true;
          marketingOptIn: boolean;
          couponCode: string | null;
        },
        EnrollResponse
      >(functions, "enrollInBatch");

      const result = await enrollInBatch({
        batchId: selectedBatch.id,
        customerEmail: email.trim() || null,
        customerName: name,
        customerPhone: phone,
        termsAccepted: true,
        marketingOptIn,
        couponCode: couponCode.trim() || null,
      });

      setBookingId(result.data.booking.id);
      setBookingReference(result.data.booking.reference);
      setHoldToken(result.data.holdToken);
      if (SHOW_PAYMENT_STEP) {
        setStep("payment");
      } else {
        // Devina asked to skip a visible payment step for now (see featureFlags.ts) -- still
        // runs the exact same real payment call and demo-confirm this app would show a button
        // for, just without a customer-facing click in between.
        await handlePay({
          bookingId: result.data.booking.id,
          bookingReference: result.data.booking.reference,
          holdToken: result.data.holdToken,
        });
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : t("batches.details.enrollError");
      setError(message);
    } finally {
      setLoading(false);
    }
  }

  async function handlePay(override?: {
    bookingId: string;
    bookingReference: string;
    holdToken: string;
  }) {
    const holdId = override?.bookingId ?? bookingId;
    const holdReference = override?.bookingReference ?? bookingReference;
    const token = override?.holdToken ?? holdToken;
    if (!holdId || !holdReference || !token || !selectedBatch) return;
    setLoading(true);
    setError(null);
    try {
      const outcome = await startPaymentForBooking({
        bookingId: holdId,
        holdToken: token,
        description: `Payment for ${selectedBatch.name}`,
        customerEmail: email,
        customerPhone: phone,
      });

      if (outcome.kind === "manual_confirmed") {
        setStep("confirmed");
        return;
      }
      if (outcome.kind === "needs_confirmation_redirect") {
        // In-page overlay gateways (Razorpay/Paytm) resolved inside startPaymentForBooking --
        // send the customer to the same webhook-driven confirmation page every gateway and
        // booking kind uses.
        router.push(`/book/confirmation?order_id=${holdId}`);
        return;
      }

      if (outcome.kind === "demo_pending") {
        if (SHOW_PAYMENT_STEP) {
          setDemoOrder({ gatewayOrderId: outcome.gatewayOrderId, amountRupees: outcome.amountRupees });
          setStep("demo_payment");
        } else {
          const confirmDemoPayment = httpsCallable<
            { gatewayOrderId: string; holdToken: string },
            { ok: true }
          >(functions, "confirmDemoPayment");
          await confirmDemoPayment({ gatewayOrderId: outcome.gatewayOrderId, holdToken: token });
          setStep("confirmed");
        }
      }
      // outcome.kind === "navigating_away": Cashfree/redirect gateways already left this page.
    } catch (err) {
      setError(t("batches.payment.payError"));
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <Header />
      <FormSplitLayout image="/images/devina/photo-04.jpg" imageAlt="Devina in a table-top yoga pose with a hula hoop">
      <div className="mx-auto w-full max-w-lg">
      {error && (
        <p className="mt-6 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      )}

      <div ref={stepContainerRef} style={{ minHeight: minStepHeight }}>
      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25, ease: "easeOut" }}
        >
          {step === "list" && (
            <BatchListStep
              batches={batches}
              loading={isLoadingBatches}
              onSelect={(batch) => {
                setSelectedBatch(batch);
                setStep("details");
              }}
            />
          )}

          {step === "details" && selectedBatch && (
            <BatchDetailsStep
              batch={selectedBatch}
              name={name}
              onNameChange={setName}
              email={email}
              onEmailChange={setEmail}
              emailVerified={emailVerified}
              onEmailVerified={() => setEmailVerified(true)}
              phone={phone}
              onPhoneChange={setPhone}
              phoneVerified={phoneVerified}
              onPhoneVerified={() => setPhoneVerified(true)}
              termsAccepted={termsAccepted}
              onTermsAcceptedChange={setTermsAccepted}
              marketingOptIn={marketingOptIn}
              onMarketingOptInChange={setMarketingOptIn}
              couponCode={couponCode}
              onCouponCodeChange={setCouponCode}
              loading={loading}
              onBack={() => setStep("list")}
              onSubmit={handleEnroll}
            />
          )}

          {step === "payment" && bookingReference && (
            <BatchPaymentStep
              bookingReference={bookingReference}
              loading={loading}
              onPay={handlePay}
            />
          )}

          {step === "demo_payment" && bookingReference && demoOrder && (
            <DemoPaymentStep
              gatewayOrderId={demoOrder.gatewayOrderId}
              bookingReference={bookingReference}
              amountRupees={demoOrder.amountRupees}
              onConfirmed={() => setStep("confirmed")}
            />
          )}

          {step === "confirmed" && bookingReference && (
            <BatchConfirmedStep bookingReference={bookingReference} />
          )}
        </motion.div>
      </AnimatePresence>
      </div>
      </div>
      </FormSplitLayout>
      <Footer />
    </>
  );
}
